import { authenticatedApi } from '../../../shared/api/apiClientUtils';

export interface SiteFileDto {
  id: string;
  name: string;
  fileSizeBytes: number;
  contentType: string;
  uploaderName: string;
  uploadedAt: string;
}

export type SiteFilesResponse = Record<string, SiteFileDto[]>;

export async function getSiteFiles(siteId: number): Promise<SiteFilesResponse> {
  const { data } = await authenticatedApi.get<SiteFilesResponse>(
    `/api/v1/sites/${siteId}/files`
  );
  return data;
}

export interface RequestFileUploadUrlRequest {
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
  contentHash: string;
  folderId?: string;
}

export interface RequestFileUploadUrlResponse {
  fileId: string;
  versionId: string;
  uploadUrl: string;
  expiresAt: string;
}

export interface ConfirmFileUploadRequest {
  fileId: string;
  versionId: string;
}

export async function requestFileUploadUrl(
  siteId: number,
  request: RequestFileUploadUrlRequest
): Promise<RequestFileUploadUrlResponse> {
  const { data } = await authenticatedApi.post<RequestFileUploadUrlResponse>(
    `/api/v1/sites/${siteId}/files/upload-url`,
    request
  );
  return data;
}

export async function confirmFileUpload(
  siteId: number,
  request: ConfirmFileUploadRequest
): Promise<void> {
  await authenticatedApi.post(
    `/api/v1/sites/${siteId}/files/confirm-upload`,
    request
  );
}

export interface FileVersionDto {
  id: string;
  versionNumber: number;
  fileSizeBytes: number;
  contentType: string;
  uploadedByName: string;
  uploadedAt: string;
  isCurrent: boolean;
  notes: string | null;
}

export interface FileDetailDto {
  id: string;
  name: string;
  contentType: string;
  fileSizeBytes: number;
  uploadedByName: string;
  uploadedAt: string;
  siteName: string;
  siteId: number;
  events: FileEventDto[];
  versions: FileVersionDto[];
}

export interface FileEventDto {
  eventType: string;
  userName: string;
  timestamp: string;
}

export interface FileDownloadUrlDto {
  url: string;
  expiresAt: string;
}

export async function getFileDetail(fileId: string): Promise<FileDetailDto> {
  const { data } = await authenticatedApi.get<FileDetailDto>(
    `/api/v1/files/${fileId}`
  );
  return data;
}

export async function getFileDownloadUrl(
  fileId: string
): Promise<FileDownloadUrlDto> {
  const { data } = await authenticatedApi.get<FileDownloadUrlDto>(
    `/api/v1/files/${fileId}/download-url`
  );
  return data;
}

export async function deleteFile(fileId: string): Promise<void> {
  await authenticatedApi.delete(`/api/v1/files/${fileId}`);
}

export interface StorageUsageDto {
  currentStorageUsageBytes: number;
  maxStorageSizeBytes: number;
  maxFileSizeBytes: number;
}

export async function getStorageUsage(): Promise<StorageUsageDto> {
  const { data } = await authenticatedApi.get<StorageUsageDto>(
    '/api/v1/storage/usage'
  );
  return data;
}

// ── Folder types ──────────────────────────────────────────────

export interface FolderDto {
  id: string;
  name: string;
  fileCount: number;
}

export interface BreadcrumbItem {
  id: string | null;
  name: string;
}

export interface FolderContentsResponse {
  folders: FolderDto[];
  files: SiteFileDto[];
  breadcrumb: BreadcrumbItem[];
}

// ── Folder API ────────────────────────────────────────────────

export async function getFolderContents(
  siteId: number,
  folderId?: string
): Promise<FolderContentsResponse> {
  const params = folderId ? `?folderId=${folderId}` : '';
  const { data } = await authenticatedApi.get<FolderContentsResponse>(
    `/api/v1/sites/${siteId}/folder-contents${params}`
  );
  return data;
}

export async function createFolder(
  siteId: number,
  request: { name: string; parentFolderId?: string }
): Promise<FolderDto> {
  const { data } = await authenticatedApi.post<FolderDto>(
    `/api/v1/sites/${siteId}/folders`,
    request
  );
  return data;
}

export async function renameFolder(
  siteId: number,
  folderId: string,
  name: string
): Promise<FolderDto> {
  const { data } = await authenticatedApi.patch<FolderDto>(
    `/api/v1/sites/${siteId}/folders/${folderId}`,
    { name }
  );
  return data;
}

export async function deleteFolder(
  siteId: number,
  folderId: string
): Promise<void> {
  await authenticatedApi.delete(`/api/v1/sites/${siteId}/folders/${folderId}`);
}

export async function renameFile(
  fileId: string,
  name: string
): Promise<{ id: string; name: string }> {
  const { data } = await authenticatedApi.patch<{ id: string; name: string }>(
    `/api/v1/files/${fileId}`,
    { name }
  );
  return data;
}

export async function moveFile(
  fileId: string,
  targetFolderId?: string
): Promise<{ id: string; name: string }> {
  const { data } = await authenticatedApi.post<{ id: string; name: string }>(
    `/api/v1/files/${fileId}/move`,
    { targetFolderId: targetFolderId ?? null }
  );
  return data;
}

// ── Bulk Delete ──────────────────────────────────────────────

export async function bulkDeleteFiles(
  siteId: number,
  fileIds: string[],
  folderIds: string[]
): Promise<void> {
  await authenticatedApi.post(`/api/v1/sites/${siteId}/bulk-delete`, {
    fileIds,
    folderIds,
  });
}

// ── Folder Move ──────────────────────────────────────────────

export async function moveFolder(
  siteId: number,
  folderId: string,
  targetParentFolderId: string | null
): Promise<void> {
  await authenticatedApi.post(
    `/api/v1/sites/${siteId}/folders/${folderId}/move`,
    { targetParentFolderId }
  );
}

// ── Accessible Sites ──────────────────────────────────────────

export interface AccessibleSiteDto {
  id: number;
  name: string;
  fileCount: number;
  lastModified: string | null;
}

export async function getAccessibleSites(): Promise<AccessibleSiteDto[]> {
  const { data } = await authenticatedApi.get<AccessibleSiteDto[]>(
    '/api/v1/files/sites'
  );
  return data;
}

// ── Version API ─────────────────────────────────────────────

export async function requestVersionUploadUrl(
  fileId: string,
  request: {
    contentType: string;
    fileSizeBytes: number;
    notes?: string;
    contentHash: string;
  }
): Promise<RequestFileUploadUrlResponse> {
  const { data } = await authenticatedApi.post<RequestFileUploadUrlResponse>(
    `/api/v1/files/${fileId}/versions/upload-url`,
    request
  );
  return data;
}

export async function confirmVersionUpload(
  fileId: string,
  request: { versionId: string }
): Promise<void> {
  await authenticatedApi.post(
    `/api/v1/files/${fileId}/versions/confirm-upload`,
    request
  );
}

export async function setCurrentVersion(
  fileId: string,
  versionId: string
): Promise<void> {
  await authenticatedApi.post(
    `/api/v1/files/${fileId}/versions/${versionId}/set-current`
  );
}

export async function getVersionDownloadUrl(
  fileId: string,
  versionId: string
): Promise<FileDownloadUrlDto> {
  const { data } = await authenticatedApi.get<FileDownloadUrlDto>(
    `/api/v1/files/${fileId}/versions/${versionId}/download-url`
  );
  return data;
}

export function uploadFileToBlobWithProgress(
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress: (loaded: number, total: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('Content-Type', contentType);

    xhr.upload.onprogress = event => {
      if (event.lengthComputable) {
        onProgress(event.loaded, event.total);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.ontimeout = () => reject(new Error('Upload timed out'));
    xhr.timeout = 300000; // 5 minutes

    xhr.send(file);
  });
}
